/**
 * Utility functions for applying watermarks to images using HTML5 Canvas
 */

/**
 * Apply a watermark to an image and return the watermarked image as a blob
 * @param {string} imageUrl - URL of the image to watermark
 * @param {Object} watermarkData - Watermark configuration
 * @param {string} watermarkData.text - Text to use as watermark (if no logo)
 * @param {string} watermarkData.logo - Logo URL to use as watermark
 * @param {string} watermarkData.brandColor - Brand color for text watermark
 * @param {string} watermarkData.font - Font family for text watermark
 * @param {Object} options - Watermark options
 * @param {string} options.position - Position: 'bottom-right', 'bottom-left', 'top-right', 'top-left', 'center'
 * @param {number} options.opacity - Opacity (0-1)
 * @param {number} options.size - Size as percentage of image (10-50)
 * @param {number} options.margin - Margin from edges in pixels
 * @returns {Promise<Blob>} - Promise that resolves to the watermarked image blob
 */
export const applyWatermarkToImage = async (imageUrl, watermarkData, options = {}) => {
  const {
    opacity = 0.3,
    size = 20,
    margin = 20
  } = options;

  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      // --- Draw logo on top-left (highlighted) ---
      if (watermarkData.logo) {
        const logoSize = size * 0.9;
        const logoMargin = margin;
        const { x: logoX, y: logoY } = calculateWatermarkPosition('top-left', canvas.width, canvas.height, logoSize, logoMargin);
        applyLogoHighlight(ctx, watermarkData.logo, logoX, logoY, logoSize, 1, canvas.width)
          .then(() => {
            // --- Draw brand name as copyright watermark on bottom-right ---
            if (watermarkData.text) {
              const textSize = size * 1.1;
              const textMargin = margin;
              const { x: textX, y: textY } = calculateWatermarkPosition('bottom-right', canvas.width, canvas.height, textSize, textMargin);
              applyCopyrightTextWatermark(ctx, `© ${watermarkData.text}`, textX, textY, textSize, opacity, watermarkData, canvas.width);
            }
            canvas.toBlob((blob) => {
              if (blob) resolve(blob);
              else reject(new Error('Failed to create watermarked image'));
            }, 'image/jpeg', 0.9);
          })
          .catch((error) => {
            console.error('Failed to apply logo watermark:', error);
            // Fallback: just draw text watermark
            if (watermarkData.text) {
              const textSize = size * 1.1;
              const textMargin = margin;
              const { x: textX, y: textY } = calculateWatermarkPosition('bottom-right', canvas.width, canvas.height, textSize, textMargin);
              applyCopyrightTextWatermark(ctx, `© ${watermarkData.text}`, textX, textY, textSize, opacity, watermarkData, canvas.width);
            }
            canvas.toBlob((blob) => {
              if (blob) resolve(blob);
              else reject(new Error('Failed to create watermarked image'));
            }, 'image/jpeg', 0.9);
          });
      } else if (watermarkData.text) {
        // Only text watermark
        const textSize = size * 1.1;
        const textMargin = margin;
        const { x: textX, y: textY } = calculateWatermarkPosition('bottom-right', canvas.width, canvas.height, textSize, textMargin);
        applyCopyrightTextWatermark(ctx, `© ${watermarkData.text}`, textX, textY, textSize, opacity, watermarkData, canvas.width);
        canvas.toBlob((blob) => {
          if (blob) resolve(blob);
          else reject(new Error('Failed to create watermarked image'));
        }, 'image/jpeg', 0.9);
      } else {
        // No watermark
        canvas.toBlob((blob) => {
          if (blob) resolve(blob);
          else reject(new Error('Failed to create watermarked image'));
        }, 'image/jpeg', 0.9);
      }
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = imageUrl;
  });
};

/**
 * Calculate watermark position based on position string
 * @param {string} position - Position string
 * @param {number} canvasWidth - Canvas width
 * @param {number} canvasHeight - Canvas height
 * @param {number} size - Watermark size percentage
 * @param {number} margin - Margin in pixels
 * @returns {Object} - { x, y } coordinates
 */
const calculateWatermarkPosition = (position, canvasWidth, canvasHeight, size, margin) => {
  const watermarkSize = Math.min(canvasWidth, canvasHeight) * (size / 100);
  
  switch (position) {
    case 'bottom-right':
      return {
        x: canvasWidth - watermarkSize - margin,
        y: canvasHeight - watermarkSize - margin
      };
    case 'bottom-left':
      return {
        x: margin,
        y: canvasHeight - watermarkSize - margin
      };
    case 'top-right':
      return {
        x: canvasWidth - watermarkSize - margin,
        y: margin
      };
    case 'top-left':
      return {
        x: margin,
        y: margin
      };
    case 'center':
      return {
        x: (canvasWidth - watermarkSize) / 2,
        y: (canvasHeight - watermarkSize) / 2
      };
    default:
      return {
        x: canvasWidth - watermarkSize - margin,
        y: canvasHeight - watermarkSize - margin
      };
  }
};

/**
 * Apply logo watermark to canvas
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {string} logoUrl - Logo URL
 * @param {number} x - X position
 * @param {number} y - Y position
 * @param {number} size - Size percentage
 * @param {number} opacity - Opacity
 * @param {number} canvasWidth - Canvas width
 * @returns {Promise} - Promise that resolves when logo is applied
 */
const applyLogoHighlight = (ctx, logoUrl, x, y, size, opacity, canvasWidth) => {
  return new Promise((resolve, reject) => {
    const logoImg = new Image();
    logoImg.crossOrigin = 'anonymous';
    logoImg.onload = () => {
      try {
        const watermarkSize = canvasWidth * (size / 100);
        const aspectRatio = logoImg.width / logoImg.height;
        let drawWidth = watermarkSize;
        let drawHeight = watermarkSize / aspectRatio;
        if (drawHeight > watermarkSize) {
          drawHeight = watermarkSize;
          drawWidth = watermarkSize * aspectRatio;
        }
        // Draw logo directly, no background or padding
        ctx.globalAlpha = opacity;
        ctx.drawImage(logoImg, x, y, drawWidth, drawHeight);
        ctx.globalAlpha = 1;
        resolve();
      } catch (error) {
        reject(error);
      }
    };
    logoImg.onerror = () => reject(new Error('Failed to load logo image'));
    logoImg.src = logoUrl;
  });
};

function drawRoundedRect(ctx, x, y, width, height, radius) {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}

/**
 * Apply text watermark to canvas
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {string} text - Text to draw
 * @param {number} x - X position
 * @param {number} y - Y position
 * @param {number} size - Size percentage
 * @param {number} opacity - Opacity
 * @param {Object} watermarkData - Watermark data with colors and fonts
 * @param {number} canvasWidth - Canvas width
 */
const applyCopyrightTextWatermark = (ctx, text, x, y, size, opacity, watermarkData, canvasWidth) => {
  const fontSize = Math.max(16, canvasWidth * (size / 100) / 8);
  const fontFamily = watermarkData.font || 'Montserrat, Inter, Arial, sans-serif';
  ctx.font = `600 ${fontSize}px ${fontFamily}`;
  ctx.textAlign = 'right';
  ctx.textBaseline = 'bottom';
  ctx.save();
  ctx.globalAlpha = opacity;
  ctx.fillStyle = '#fff';
  // Compose watermark text with year
  const watermarkText = `${text} 2025`;
  // Move text as close as possible to the bottom-right (5px margin)
  ctx.fillText(watermarkText, canvasWidth - 5, ctx.canvas.height - 5);
  ctx.restore();
};

/**
 * Apply watermark to multiple images
 * @param {Array} imageUrls - Array of image URLs
 * @param {Object} watermarkData - Watermark configuration
 * @param {Object} options - Watermark options
 * @returns {Promise<Array>} - Promise that resolves to array of watermarked image blobs
 */
export const applyWatermarkToMultipleImages = async (imageUrls, watermarkData, options = {}) => {
  const watermarkedImages = [];
  
  for (const imageUrl of imageUrls) {
    try {
      const watermarkedBlob = await applyWatermarkToImage(imageUrl, watermarkData, options);
      watermarkedImages.push(watermarkedBlob);
    } catch (error) {
      console.error('Failed to watermark image:', imageUrl, error);
      // Return original image if watermarking fails
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      watermarkedImages.push(blob);
    }
  }
  
  return watermarkedImages;
};

/**
 * Get default watermark options
 * @returns {Object} Default watermark options
 */
export const getDefaultWatermarkOptions = () => ({
  position: 'bottom-right',
  opacity: 0.3,
  size: 20,
  margin: 20
});

/**
 * Validate watermark options
 * @param {Object} options - Watermark options to validate
 * @returns {Object} Validated options with defaults for invalid values
 */
export const validateWatermarkOptions = (options = {}) => {
  const defaults = getDefaultWatermarkOptions();
  const validPositions = ['bottom-right', 'bottom-left', 'top-right', 'top-left', 'center'];
  
  return {
    position: validPositions.includes(options.position) ? options.position : defaults.position,
    opacity: Math.max(0, Math.min(1, options.opacity || defaults.opacity)),
    size: Math.max(5, Math.min(50, options.size || defaults.size)),
    margin: Math.max(0, Math.min(100, options.margin || defaults.margin))
  };
};

/**
 * Create a download link for a watermarked image
 * @param {Blob} blob - Watermarked image blob
 * @param {string} filename - Filename for download
 */
export const downloadWatermarkedImage = (blob, filename = 'watermarked-image.jpg') => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}; 